/**
 * Tags service — the `tags` rows (a label's name, slug, color and place in the
 * tag hierarchy), merging duplicate tags, and dismissing a suggested duplicate
 * group. Tagging a record is a different table (`record_taggings`) and goes
 * through the relation writes, not here.
 *
 * `merge`, `mergeInto` and `dismissGroup` call site-admin-only database
 * functions: run them through an Auger whose transport carries that role.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { AS_ADMIN } from "../asAdmin.js"
import type { TagValueFieldsFragment } from "../gql/generated/graphql.js"
import {
  deleteTag,
  deleteTags,
  dismissTagGroup,
  insertTag,
  insertTags,
  mergeTags,
  updateTag,
} from "../gql/tags.js"
import type { Mutate, Query } from "../index.js"

/** The tags on a record's `taggings` rows (each hopped to its `tag`), sorted by name. */
export function extractTags(
  taggings: ReadonlyArray<{ tag: TagValueFieldsFragment }> | null | undefined
): TagValueFieldsFragment[] {
  if (!taggings) return []
  return taggings
    .map((tagging) => tagging.tag)
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** A tags row as create, createMany and merge return it: TagValueFields. */
export type TagItem = NonNullable<ResultOf<typeof insertTag>["insert_tags_one"]>

/** A tag with its immediate parent, as `update` returns it. */
export type TagWithParentItem = NonNullable<ResultOf<typeof updateTag>["update_tags_by_pk"]>

export interface CreateTagInput {
  name: string
  /** Omit to let the database generate it (set_default_tag_slug). */
  slug?: string
  color?: string | null
  parent_id?: number | null
}

/** Only the keys present are written. */
export interface UpdateTagPatch {
  name?: string
  slug?: string
  color?: string | null
  parent_id?: number | null
}

export interface RemoveTagsResult {
  removedCount: number
  removedIds: number[]
  missingIds: number[]
}

/** Slug is OMITTED unless the caller supplied a non-blank one. Clients used to
 *  generate it, stripping accents where Postgres transliterates them — "Café"
 *  became "caf" rather than "cafe", and 69 production tags carry a slug mangled
 *  that way. The database owns the rule (set_default_tag_slug ->
 *  generate_unique_tag_slug); trimmed, because a " x " slug would skip the
 *  trigger and then miss every slug-keyed lookup. */
function insertObject(input: CreateTagInput): Record<string, unknown> {
  const slug = input.slug?.trim()
  const object: Record<string, unknown> = { name: input.name }
  if (slug) object.slug = slug
  if (input.color !== undefined) object.color = input.color
  if (input.parent_id !== undefined) object.parent_id = input.parent_id
  return object
}

export interface MergeIntoResult {
  /** Tags folded into the keep, in order. */
  merged: number[]
  /** Tags left alone because they are ANCESTORS of the keep — merge_tags
   *  refuses to fold a tag into its own descendant (it would corrupt the tree).
   *  Picking one of them as the keep instead resolves it. */
  skipped: number[]
  /** The tag whose merge failed for any other reason; merging stopped there,
   *  and the tags after it were not attempted. */
  failed: { id: number; message: string } | null
}

const ANCESTOR_CYCLE = /Cannot merge tag \d+ into its own descendant \d+/

export function createTagsService({ mutate }: { query: Query; mutate: Mutate }) {
  const create = async (input: CreateTagInput): Promise<TagItem> => {
    const row = (await mutate(insertTag, { object: insertObject(input) })).insert_tags_one
    if (!row) throw new Error("Failed to create tag")
    return row
  }

  /** Rows come back in database order; one that was filtered (RLS, a trigger)
   *  is simply absent, so the result may be shorter than `inputs`. */
  const createMany = async (inputs: CreateTagInput[]): Promise<TagItem[]> => {
    if (!inputs.length) return []
    const result = (await mutate(insertTags, { objects: inputs.map(insertObject) })).insert_tags
    if (!result) throw new Error("insertTags: Hasura returned no insert_tags result")
    return result.returning
  }

  const update = async (id: number, patch: UpdateTagPatch): Promise<TagWithParentItem> => {
    const row = (await mutate(updateTag, { id, _set: patch })).update_tags_by_pk
    if (!row) throw new Error(`Tag with id ${id} not found`)
    return row
  }

  /** Resolves to the deleted row's id, or null when no such tag existed —
   *  deleting an absent tag is not an error. */
  const remove = async (id: number): Promise<{ id: number } | null> => {
    return (await mutate(deleteTag, { id }, AS_ADMIN)).delete_tags_by_pk ?? null
  }

  /** `missingIds` are the requested ids that deleted nothing (never existed,
   *  or hidden from the caller). */
  const removeMany = async (ids: number[]): Promise<RemoveTagsResult> => {
    if (!ids.length) return { removedCount: 0, removedIds: [], missingIds: [] }

    const result = (await mutate(deleteTags, { ids }, AS_ADMIN)).delete_tags
    // Fail loud on a null mutation root rather than reporting "removed nothing".
    if (!result) throw new Error("deleteTags: Hasura returned no delete_tags result")

    const removedIds = result.returning.map((row) => row.id)
    const removed = new Set(removedIds)
    return {
      removedCount: result.affected_rows,
      removedIds,
      missingIds: ids.filter((id) => !removed.has(id)),
    }
  }

  /** Fold `sourceId` into `targetId`; returns the target. */
  const merge = async (sourceId: number, targetId: number): Promise<TagItem> => {
    const row = (await mutate(mergeTags, { sourceId, targetId })).merge_tags?.[0]
    if (!row) throw new Error(`merge: merging tag ${sourceId} into ${targetId} returned no tag`)
    return row
  }

  /**
   * Fold every tag in `tagIds` except `keepId` into `keepId`, one at a time —
   * each merge moves rows onto the same target, so running them in parallel
   * could race. A tag that is an ancestor of the keep is skipped, not fatal,
   * since ancestor/descendant pairs land in the same duplicate group on real
   * data. Any other failure stops the run and is reported in `failed`.
   */
  const mergeInto = async (tagIds: number[], keepId: number): Promise<MergeIntoResult> => {
    const result: MergeIntoResult = { merged: [], skipped: [], failed: null }
    for (const id of tagIds) {
      if (id === keepId) continue
      try {
        await merge(id, keepId)
        result.merged.push(id)
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        if (ANCESTOR_CYCLE.test(message)) {
          result.skipped.push(id)
          continue
        }
        result.failed = { id, message }
        return result
      }
    }
    return result
  }

  /** Mark `tagIds` as not duplicates of each other. The group is stored
   *  sorted, so the same set dismisses the same row whatever order it came in. */
  const dismissGroup = async (tagIds: number[]): Promise<void> => {
    await mutate(dismissTagGroup, { tagIds: [...tagIds].sort((a, b) => a - b) })
  }

  return { create, createMany, update, remove, removeMany, merge, mergeInto, dismissGroup }
}
